import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip, statusTone } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { fmtEur, fmtDate } from '../../lib/utils';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ContactDetailMobile } from './ContactDetailMobile';
import {
  canWriteContatti, contattoQuery, TIPO_LABEL,
  type ContattoCompleto, type TrattativaDTO,
} from './contactsApi';

/**
 * La scheda di un contatto: la catena intera in una colonna sola.
 *
 * L'ordine e' quello con cui si guarda un soggetto prima di chiamarlo: chi e'
 * e come si raggiunge, cosa stiamo trattando, cosa gli stiamo facendo, cosa
 * paga ogni mese, cosa ci deve. E se ci vende anche lui, le due facce insieme
 * con il saldo netto: su Tridentum Auto sono 71.250 di fatture ricevute contro
 * tre emesse, e guardarne una sola racconta il contrario di quello che e'
 * successo.
 *
 * I totali delle fatture arrivano dal server calcolati su TUTTE le fatture.
 * Sommarli nel browser dalle ultime caricate per la tabella faceva dire "0 di
 * scaduto" a Emmequadro, che ne ha 18.580, e a New Omnia, che ne ha 6.100.
 */
/**
 * Sul telefono la scheda è ContactDetailMobile (come un contatto dell'iPhone:
 * gesti rapidi, poi solo le righe che hanno un dato). Sul computer resta questa.
 */
export function ContactDetailPage() {
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const isMobile = useIsMobile();
  if (!kind || !id) return <Navigate to="/crm" replace />;
  return isMobile ? <div className="page"><ContactDetailMobile kind={kind} id={id} /></div> : <ContactDetailDesktop />;
}

function ContactDetailDesktop() {
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteContatti(user);

  const { data: c, isLoading, isError } = useQuery({
    ...contattoQuery(kind ?? '', id ?? ''),
    enabled: !!kind && !!id,
    retry: false,
  });

  const promuovi = useMutation({
    mutationFn: () => api.post<{ key: string; trattativeCollegate?: number }>('/contacts/promuovi', { kind, id }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      toast.ok('Ora è un cliente', r.trattativeCollegate ? `${r.trattativeCollegate} trattative collegate alla scheda` : undefined);
      navigate(`/contatti/${r.key}`, { replace: true });
    },
    onError: (e: any) => toast.alert('Non promosso', e?.message ?? 'Riprova'),
  });

  if (!kind || !id) return <Navigate to="/crm" replace />;
  if (isError) {
    return (
      <div className="page">
        <Breadcrumb items={[{ label: 'Contatti', to: '/crm' }, { label: 'Scheda' }]} />
        <div className="max-w-[480px] mx-auto py-20 text-center">
          <h1 className="t-h1">Contatto non trovato</h1>
          <div className="mute mt-2">Forse è stato eliminato, o il link è sbagliato.</div>
        </div>
      </div>
    );
  }
  // header="chips" e non "avatar": questa scheda l'avatar non ce l'ha, e lo
  // scheletro con il cerchio da 56px spostava tutto il blocco del titolo di 68px
  // verso destra — poi, all'arrivo dei dati, tornava indietro. Lo scheletro
  // serve proprio a non far saltare niente.
  if (isLoading || !c) return <DetailSkeleton header="chips" />;

  const a = c.anagrafica;
  const aperte = c.trattative.filter((t) => t.outcome === 'APERTO');
  const chiuse = c.trattative.filter((t) => t.outcome !== 'APERTO');
  const potenziale = c.tipi.includes('POTENZIALE');
  // Quanto entra ogni mese: i contratti scritti in Amministrazione piu' gli
  // abbonamenti riportati a mese.
  const mensile = c.canoneMensile + c.mensileAbbonamenti;
  // Se l'azienda non ha una mail sua ma un referente ce l'ha, si mostra quella
  // dicendo di chi e': meglio "scrivi a Giuliano" che "non ce l'abbiamo".
  const ripiego = c.referenti.find((r) => r.email) ?? c.referenti.find((r) => r.phone) ?? null;

  return (
    // m-detail: sotto i 768px la scheda e' una colonna sola (mobile.md §7).
    // Sul computer la classe non dichiara niente.
    <div className="page m-detail">
      <Breadcrumb items={[{ label: 'Contatti', to: '/crm' }, { label: a.shortName }]} />

      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          <h1 className="ed-title truncate">{c.name}</h1>
          {/* Solo i pezzi che esistono: su un potenziale appena nato non c'e'
              niente da scrivere qui, e una riga di trattini fa sembrare rotta
              la scheda. */}
          <div className="detail-meta mt-2 truncate">
            {a.vat && <span className="mono">{a.vat}</span>}
            {a.vat && (a.city || a.sector) && <span className="mute-2">·</span>}
            {(a.city || a.sector) && <span>{[a.city, a.sector].filter(Boolean).join(' · ')}</span>}
            {a.sinceYear && <><span className="mute-2">·</span><span>dal {a.sinceYear}</span></>}
            {a.accountManager && (
              <>
                <span className="mute-2">·</span>
                <span><span className="mute-2 mr-1.5">Resp.</span><span className="v">{a.accountManager.firstName[0]}. {a.accountManager.lastName}</span></span>
              </>
            )}
          </div>
          {/* Le etichette: un soggetto puo' essere piu' cose insieme, ed e'
              esattamente il punto per cui questa sezione esiste. */}
          <div className="flex flex-wrap gap-1.5 mt-2.5 items-center">
            {c.tipi.map((t) => (
              <Chip key={t} tone={t === 'CLIENTE' ? 'ok' : t === 'POTENZIALE' ? 'info' : 'ghost'} dot>{TIPO_LABEL[t]}</Chip>
            ))}
            {a.status !== 'ATTIVO' && a.status !== 'LEAD' && <Chip tone={statusTone(a.status)}>{a.status.replace('_', ' ')}</Chip>}
          </div>
        </div>
        {/* d-only: sul telefono queste due azioni scendono nella barra in
            fondo, dove arriva il pollice (mobile.md §4). */}
        <div className="flex gap-2 pb-1 shrink-0 d-only">
          {canWrite && potenziale && (
            <button className="btn btn-primary" disabled={promuovi.isPending} onClick={() => promuovi.mutate()}>
              <Icon name="check" size={12} /> Diventa cliente
            </button>
          )}
          {c.kind === 'client' && (
            <button className="btn" onClick={() => navigate(`/clients/${c.id}`)}>
              Scheda estesa
            </button>
          )}
        </div>
      </div>

      {/* I quattro numeri che dicono come sta il rapporto. Nudi, senza scatole. */}
      <div className="stat-band">
        <div>
          <div className="st-label">Fatturato</div>
          <div className="st-value mono">€ {fmtEur(c.fatture.totale)}</div>
          {/* Le annullate si dicono, ma non si sommano: se il numero in alto
              non torna con l'elenco qui sotto, la prima cosa da sapere e'
              quante ce ne sono di stornate. */}
          <div className="st-delta">
            {c.fatture.count} fatture{c.fatture.annullate > 0 && `, ${c.fatture.annullate} annullate`}
          </div>
        </div>
        <div>
          <div className="st-label">Scaduto</div>
          <div className="st-value mono" style={c.fatture.scaduto > 0 ? { color: 'var(--alert)' } : undefined}>€ {fmtEur(c.fatture.scaduto)}</div>
          <div className="st-delta">{c.fatture.daIncassare > 0 ? `+ € ${fmtEur(c.fatture.daIncassare)} da incassare` : 'niente da incassare'}</div>
        </div>
        <div>
          <div className="st-label">Ogni mese</div>
          <div className="st-value mono">€ {fmtEur(mensile)}</div>
          {/* Singolare e plurale scritti giusti: "1 canoni" su una scheda che
              Lorenzo guarda ogni giorno si nota, e fa sembrare tutto sciatto. */}
          <div className="st-delta">
            {c.abbonamenti.length
              ? `${c.abbonamenti.length} ${c.abbonamenti.length === 1 ? 'abbonamento' : 'abbonamenti'}`
              : c.canoni.length
                ? `${c.canoni.length} ${c.canoni.length === 1 ? 'canone' : 'canoni'}`
                : 'nessun canone'}
          </div>
        </div>
        <div>
          <div className="st-label">Potenziale</div>
          <div className="st-value mono">€ {fmtEur(c.potenziale)}</div>
          <div className="st-delta">{aperte.length ? `${aperte.length} in corso` : 'niente aperto'}</div>
        </div>
      </div>

      {/* ct-detail: solo un aggancio per contacts.css (i bersagli da 44px sui
          recapiti, sul telefono). Sul computer non fa niente. */}
      <div className="tk-detail ct-detail">
        {/* ── COME SI RAGGIUNGE ─────────────────────────────────────────── */}
        <div className="data-hd"><span className="t-micro">Come si raggiunge</span></div>
        <div className="ed-kv">
          <KvRow label="Email">
            {c.recapiti.email
              ? <a className="link" href={`mailto:${c.recapiti.email}`}>{c.recapiti.email}</a>
              : ripiego?.email
                ? <><a className="link" href={`mailto:${ripiego.email}`}>{ripiego.email}</a><span className="mute-2 ml-2 text-[12px]">di {ripiego.fullName}</span></>
                : <span className="mute">Non ce l'abbiamo</span>}
          </KvRow>
          <KvRow label="Telefono">
            {c.recapiti.phone
              ? <a className="link mono" href={`tel:${c.recapiti.phone}`}>{c.recapiti.phone}</a>
              : ripiego?.phone
                ? <><a className="link mono" href={`tel:${ripiego.phone}`}>{ripiego.phone}</a><span className="mute-2 ml-2 text-[12px]">di {ripiego.fullName}</span></>
                : <span className="mute">Non ce l'abbiamo</span>}
          </KvRow>
          {a.pec && <KvRow label="PEC"><span className="mono">{a.pec}</span></KvRow>}
          {a.website && <KvRow label="Sito"><a className="link" href={a.website} target="_blank" rel="noreferrer">{a.website}</a></KvRow>}
          {(a.addressLine || a.city) && (
            <KvRow label="Sede">{[a.addressLine, a.zip, a.city, a.province].filter(Boolean).join(', ')}</KvRow>
          )}
          {a.skills && a.skills.length > 0 && <KvRow label="Fa">{a.skills.join(', ')}</KvRow>}
        </div>

        {/* I referenti: le persone dentro l'azienda. Stanno qui, non schiacciate
            sulla mail dell'azienda, perche' cambiano ruolo e cambiano posto. */}
        {c.referenti.length > 0 && (
          <>
            <div className="data-hd mt-4">
              <span className="t-micro">Referenti</span>
              <span className="mono mute text-[12px]">{c.referenti.length}</span>
            </div>
            {/* tbl--card: sotto i 768px ogni riga diventa una scheda. */}
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {/* m-static: la riga non si apre (un referente non ha una
                    scheda sua), quindi in scheda non deve portare il chevron
                    che vuol dire "si apre". */}
                {c.referenti.map((r) => (
                  <tr key={r.id} className="m-static">
                    <td data-m="hd" className="cell-2l">
                      <div className="p">{r.fullName}{r.isPrimary && <span className="mute-2 ml-2 text-[12px]">principale</span>}</div>
                      {r.role && <div className="s">{r.role}</div>}
                    </td>
                    <td data-m="row" data-l="Email">{r.email ? <a className="link" href={`mailto:${r.email}`}>{r.email}</a> : <span className="mute-2">—</span>}</td>
                    <td data-m="key" data-l="Telefono" className="mono">{r.phone ?? <span className="mute-2">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="ed-divider" />

        {/* ── TRATTATIVE ────────────────────────────────────────────────── */}
        <div className="data-hd">
          <span className="t-micro">Trattative</span>
          <span className="mono mute text-[12px]">{c.trattative.length}</span>
        </div>
        {c.trattative.length === 0 ? (
          <div className="mute text-[13px] py-2">Nessuna trattativa.</div>
        ) : (
          <table className="tbl tbl--ed tbl--card">
            <tbody>
              {[...aperte, ...chiuse].map((t) => <RigaTrattativa key={t.id} t={t} onOpen={() => navigate(`/trattative/${t.id}`)} />)}
            </tbody>
          </table>
        )}

        {/* ── PROGETTI ──────────────────────────────────────────────────── */}
        {c.progetti.length > 0 && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="t-micro">Progetti</span>
              <span className="mono mute text-[12px]">{c.progetti.length}</span>
            </div>
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {c.progetti.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                    <td data-m="hd" className="cell-2l">
                      <div className="p">{p.name}</div>
                      <div className="s mono">{p.code}</div>
                    </td>
                    <td data-m="key" data-l="A che punto è"><Chip tone={statusTone(p.status)} dot>{p.status.replace('_', ' ').toLowerCase()}</Chip></td>
                    <td data-m="key" data-l="Consegna">{p.deadline ? fmtDate(p.deadline) : <span className="mute-2">—</span>}</td>
                    <td data-m="key" data-l="Valore" className="num mono">{p.revenue > 0 ? `€ ${fmtEur(p.revenue)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* ── ABBONAMENTI ───────────────────────────────────────────────── */}
        {(c.abbonamenti.length > 0 || c.canoni.length > 0) && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="t-micro">Abbonamenti</span>
              <span className="mono mute text-[12px]">€ {fmtEur(mensile, true)} al mese</span>
            </div>
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {/* m-static: queste righe non si aprono, niente chevron. */}
                {c.abbonamenti.map((s) => (
                  <tr key={s.id} className="m-static">
                    <td data-m="hd" className="cell-2l">
                      <div className="p">{s.planLabel}</div>
                      <div className="s">{s.provider.toLowerCase()} · {s.type.toLowerCase()}</div>
                    </td>
                    <td data-m="key" data-l="Stato"><Chip tone={s.status === 'ACTIVE' ? 'ok' : s.status === 'PAST_DUE' ? 'alert' : 'ghost'} dot>{s.status.toLowerCase()}</Chip></td>
                    {/* Il rinnovo e' la data che serve davvero: dice quando
                        ricapita l'addebito e quando si puo' rinegoziare. */}
                    <td data-m="key" data-l="Rinnovo">{s.currentPeriodEnd ? <>rinnova il <span className="mono">{fmtDate(s.currentPeriodEnd)}</span></> : <span className="mute-2">—</span>}</td>
                    <td data-m="key" data-l="Quanto" className="num mono">€ {fmtEur(s.amount, true)}<span className="mute-2 ml-1 text-[12px]">/{s.billingCycle === 'YEARLY' ? 'anno' : s.billingCycle === 'QUARTERLY' ? 'trim.' : 'mese'}</span></td>
                  </tr>
                ))}
                {c.canoni.map((r) => (
                  <tr key={r.id} className="m-static">
                    <td data-m="hd" className="cell-2l">
                      <div className="p">{r.name}</div>
                      <div className="s">canone in amministrazione</div>
                    </td>
                    <td data-m="key" data-l="Stato"><Chip tone={r.inVigore ? 'ghost' : 'info'} dot>{r.inVigore ? 'contratto' : 'non in vigore'}</Chip></td>
                    <td data-m="key" data-l="Rinnovo">
                      {r.inVigore
                        ? <>prossima il <span className="mono">{fmtDate(r.nextDueDate)}</span></>
                        : <span className="mute">fuori dal totale in cima</span>}
                    </td>
                    <td data-m="key" data-l="Quanto" className="num mono">€ {fmtEur(r.amountNet, true)}<span className="mute-2 ml-1 text-[12px]">/{r.cycle === 'YEARLY' ? 'anno' : r.cycle === 'QUARTERLY' ? 'trim.' : 'mese'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* ── FATTURE ───────────────────────────────────────────────────── */}
        {c.fatture.count > 0 && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="t-micro">Fatture</span>
              <span className="mono mute text-[12px]">{c.fatture.count} in tutto</span>
            </div>
            <div className="ed-kv">
              <KvRow label="Incassato" hint="su tutte le fatture, non solo le ultime">
                <span className="mono">€ {fmtEur(c.fatture.incassato, true)}</span>
                <span className="mute-2 ml-2 text-[12px]">di cui € {fmtEur(c.fatture.incassatoYtd, true)} quest'anno</span>
              </KvRow>
              <KvRow label="Da incassare">
                <span className="mono">€ {fmtEur(c.fatture.daIncassare, true)}</span>
              </KvRow>
              <KvRow label="Scaduto">
                <span className="mono" style={c.fatture.scaduto > 0 ? { color: 'var(--alert)' } : undefined}>€ {fmtEur(c.fatture.scaduto, true)}</span>
              </KvRow>
            </div>
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {c.ultimeFatture.map((f) => (
                  <tr key={f.id} onClick={() => navigate(`/invoices/${f.id}`)}>
                    <td data-m="hd" className="cell-2l">
                      <div className="p mono">{f.number ?? 'senza numero'}</div>
                      <div className="s">{f.project?.code ?? '—'}</div>
                    </td>
                    <td data-m="key" data-l="Stato"><Chip tone={statusTone(f.status)} dot>{f.status.toLowerCase()}</Chip></td>
                    <td data-m="key" data-l="Emissione">{fmtDate(f.issueDate)}</td>
                    <td data-m="key" data-l="Totale" className="num mono">€ {fmtEur(f.total, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {c.fatture.count > c.ultimeFatture.length && (
              <div className="mute text-[12px] mt-2">
                Le ultime {c.ultimeFatture.length}. I totali qui sopra guardano tutte e {c.fatture.count}
                {c.fatture.annullate > 0 ? `, tolte le ${c.fatture.annullate} annullate.` : '.'}
              </div>
            )}
          </>
        )}

        {/* ── ANCHE FORNITORE ───────────────────────────────────────────── */}
        {c.fornitore && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="t-micro">Ci fattura anche lui</span>
              <span className="mono mute text-[12px]">
                {c.fornitore.count} documenti{c.fornitore.annullate > 0 && `, ${c.fornitore.annullate} annullati`}
              </span>
            </div>
            <div className="ed-kv">
              <KvRow label="Ricevute da lui"><span className="mono">€ {fmtEur(c.fornitore.totale, true)}</span></KvRow>
              <KvRow label="Ancora da pagare">
                <span className="mono" style={c.fornitore.daPagare > 0 ? { color: 'var(--warn)' } : undefined}>€ {fmtEur(c.fornitore.daPagare, true)}</span>
              </KvRow>
              {/* Il saldo netto: chi deve a chi, guardando le due facce insieme. */}
              <KvRow label="Saldo del rapporto" hint="quello che gli abbiamo fatturato meno quello che ci ha fatturato">
                <span className="mono" style={{ color: c.fornitore.saldo < 0 ? 'var(--alert)' : undefined }}>
                  € {fmtEur(c.fornitore.saldo, true)}
                </span>
                <span className="mute-2 ml-2 text-[12px]">{c.fornitore.saldo < 0 ? 'ci compra meno di quanto ci vende' : 'ci compra più di quanto ci vende'}</span>
              </KvRow>
            </div>
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {/* m-static: le fatture ricevute non hanno una scheda qui. */}
                {c.fornitore.righe.map((p) => (
                  <tr key={p.id} className="m-static">
                    <td data-m="hd" className="cell-2l">
                      <div className="p mono">{p.number ?? 'senza numero'}</div>
                      <div className="s">{p.description ?? '—'}</div>
                    </td>
                    <td data-m="key" data-l="Stato"><Chip tone={statusTone(p.status)} dot>{p.status.replace('_', ' ').toLowerCase()}</Chip></td>
                    <td data-m="key" data-l="Emissione">{fmtDate(p.issueDate)}</td>
                    <td data-m="key" data-l="Totale" className="num mono">€ {fmtEur(p.total, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2 mt-3">
              {c.fornitore.schede.map((s) => (
                <button key={s.id} className="btn btn-sm btn-ghost" onClick={() => navigate(`/suppliers/${s.id}`)}>
                  Scheda fornitore · {s.fullName}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Telefono: le stesse azioni della cima, dove arriva il pollice. Se non
          ce n'e' nessuna la barra non si monta: una striscia vuota in fondo
          allo schermo e' peggio che niente. */}
      {((canWrite && potenziale) || c.kind === 'client') && (
        <div className="m-actionbar m-only">
          {canWrite && potenziale && (
            <button className="btn btn-primary" disabled={promuovi.isPending} onClick={() => promuovi.mutate()}>
              <Icon name="check" size={12} /> Diventa cliente
            </button>
          )}
          {c.kind === 'client' && (
            <button className="btn" onClick={() => navigate(`/clients/${c.id}`)}>Scheda estesa</button>
          )}
        </div>
      )}
    </div>
  );
}

function RigaTrattativa({ t, onOpen }: { t: TrattativaDTO; onOpen: () => void }) {
  return (
    <tr onClick={onOpen}>
      <td data-m="hd" className="cell-2l">
        <div className="p">{t.title}</div>
        <div className="s">{t.pipeline}</div>
      </td>
      <td data-m="key" data-l="Step">
        <Chip tone={t.outcome === 'VINTO' ? 'ok' : t.outcome === 'PERSO' ? 'ghost' : 'info'} dot>{t.stage}</Chip>
      </td>
      {/* 12 e non 12,5: la scala dichiarata del progetto ha cinque corpi, e
          12,5 non e' uno di quelli. */}
      <td data-m="key" data-l="A che punto è">
        {t.projectCode
          ? <span className="mute text-[12px]">è diventata {t.projectCode}</span>
          : t.expectedCloseDate ? <span className="mute text-[12px]">chiusura {fmtDate(t.expectedCloseDate)}</span> : <span className="mute-2">—</span>}
      </td>
      <td data-m="key" data-l="Valore" className="num mono">€ {fmtEur(t.amount)}</td>
    </tr>
  );
}

function KvRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="ed-kv-row">
      <div className="k">{label}{hint && <div className="tk-kv-hint">{hint}</div>}</div>
      <div className="v">{children}</div>
    </div>
  );
}

export type { ContattoCompleto };
